import { createSlice } from '@reduxjs/toolkit';

import axios, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  usage: null,
  isLoading: false,
  error: null,
};

const slice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    // START LOADING
    startLoading (state) {
      state.isLoading = true;
      state.error = null;
    },

    // HAS ERROR
    hasError (state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET USAGE SUCCESS
    getUsageSuccess (state, action) {
      state.isLoading = false;
      state.usage = action.payload;
      state.error = null;
    },

    // LOCAL: decrement one AI credit (add-on first, then monthly)
    decrementAICredit (state) {
      if (!state.usage) return;
      const addOn = state.usage.usage.aiCreditsAddOn || 0;
      if (addOn > 0) {
        state.usage.usage.aiCreditsAddOn = addOn - 1;
      } else {
        state.usage.usage.aiCredits = Math.max(0, state.usage.usage.aiCredits - 1);
      }
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { decrementAICredit } = slice.actions;

// ----------------------------------------------------------------------

/**
 * Read how much of the plan the seller is using. The listing counts come straight from their
 * listings, so anything that adds or removes listings has to call this again — the sidebar
 * usage card shows whatever landed here last.
 */
export function getUsage () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(endpoints.usage);
      dispatch(slice.actions.getUsageSuccess(response.data));
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch usage data'));
    }
  };
}

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectUsage = (state) => state.plan.usage;

export const selectLoading = (state) => state.plan.isLoading;

export const selectError = (state) => state.plan.error;

export const selectCanCreateListing = (state) => {
  const { usage } = state.plan;
  if (!usage) return false;
  return usage.usage.activeListings < usage.limits.activeListings;
};

export const selectHasAICredits = (state) => {
  const { usage } = state.plan;
  if (!usage) return false;
  return (usage.usage.aiCredits + (usage.usage.aiCreditsAddOn || 0)) > 0;
};

export const selectCanCreateDraft = (state) => {
  const { usage } = state.plan;
  if (!usage) return true; // permissive default
  const draftLimit = usage.limits?.draftListings;
  // V1 plans have draftListings: null (unlimited)
  if (draftLimit === null || draftLimit === undefined) return true;
  return (usage.usage?.draftListings || 0) < draftLimit;
};
